import React from 'react';
import { X } from 'lucide-react';

// Data-driven landscape comparison table for the Buyer's Guide (LP-006).
// Desktop: one CSS grid (label column + 4 category columns) so every row
// shares a row-track and the divider lines run unbroken across the table.
// Mobile: stacks into one card per category instead of a horizontal-scroll
// table, since a 5-column grid can't read well on a phone.
const ROWS = [
  { label: 'Best for', key: 'bestFor' },
  { label: 'Records', key: 'records' },
  { label: 'Maintenance cost', key: 'maintenance' },
  { label: 'Strengths', key: 'strengths' },
  { label: 'Limitations', key: 'limitations' },
  { label: 'Risk if abandoned', key: 'risk' },
];

const Dots = ({ filled, label }) => (
  <div className="flex items-center gap-2">
    <div className="flex items-center gap-1">
      {[0, 1, 2, 3, 4].map((i) =>
        i < filled ? (
          <X key={i} size={11} strokeWidth={3} className="text-[#B5453F]" />
        ) : (
          <span key={i} className="w-[7px] h-[7px] rounded-full bg-[#E4DAC9]" />
        )
      )}
    </div>
    <span className="text-[#6B655C] text-[13px]">{label}</span>
  </div>
);

const cellBg = (col) => (col.highlight ? 'bg-[#EDF1E6]' : 'bg-[#FDF8F3]');

const CardHeader = ({ col, size = 'lg' }) => {
  const Icon = col.icon;
  const big = size === 'lg';
  return (
    <div className={`relative text-center border-b border-[#D3C6AC] ${big ? 'h-full px-6 pt-9 pb-7' : 'px-6 pt-8 pb-6'}`}>
      {col.highlight && (
        <span className="absolute top-4 right-4 bg-[#3A4A32] text-[#F4EEDF] text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-md">
          Archen™
        </span>
      )}
      <div className={`mx-auto rounded-full bg-white/70 flex items-center justify-center ${big ? 'w-16 h-16 mb-5' : 'w-14 h-14 mb-4'}`}>
        <Icon size={big ? 26 : 22} strokeWidth={1.4} className="text-[#3A2F26]" />
      </div>
      <p className={`text-[#1F1812] font-semibold uppercase tracking-[0.03em] leading-snug ${big ? 'text-[15px]' : 'text-[14px]'}`}>{col.title}</p>
      <p className={`mt-3 text-[#6B655C] leading-[1.5] ${big ? 'text-[13.5px]' : 'text-[13px]'}`}>{col.desc}</p>
    </div>
  );
};

export const ComparisonMatrix = ({ columns }) => (
  <>
    {/* Mobile/tablet: one stacked card per category */}
    <div className="md:hidden space-y-6">
      {columns.map((col, ci) => (
        <div
          key={col.title}
          data-testid={`comparison-column-${ci}`}
          className={`rounded-2xl border overflow-hidden ${col.highlight ? 'border-2 border-[#5C6B4E]' : 'border-[#D3C6AC]'} ${cellBg(col)}`}
        >
          <CardHeader col={col} size="sm" />
          <div className="divide-y divide-[#D3C6AC]">
            {ROWS.map((row) => (
              <div key={row.key} className="px-6 py-4">
                <p className="text-[#A89880] text-[10px] font-semibold tracking-[0.12em] uppercase mb-1.5">{row.label}</p>
                {row.key === 'maintenance' ? (
                  <Dots filled={col.maintenance.dots} label={col.maintenance.label} />
                ) : (
                  <p className="text-[#3D3A36] text-[13.5px] leading-[1.5]">{col[row.key]}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* Desktop: single grid so divider lines run unbroken across all columns */}
    <div className="hidden md:block overflow-x-auto rounded-2xl border border-[#D3C6AC]">
      <div className="grid min-w-[920px]" style={{ gridTemplateColumns: '160px repeat(4, minmax(190px, 1fr))' }}>
        <div className="border-b border-[#D3C6AC] bg-[#FBF6EF]" />
        {columns.map((col, ci) => (
          <div key={col.title} data-testid={`comparison-column-desktop-${ci}`} className={`border-l border-[#D3C6AC] ${cellBg(col)}`}>
            <CardHeader col={col} />
          </div>
        ))}

        {ROWS.map((row, ri) => {
          const last = ri === ROWS.length - 1;
          return (
            <React.Fragment key={row.key}>
              <div className={`px-4 py-5 flex items-start bg-[#FBF6EF] ${last ? '' : 'border-b'} border-[#D3C6AC]`}>
                <p className="text-[#A89880] text-[10.5px] font-semibold tracking-[0.12em] uppercase">{row.label}</p>
              </div>
              {columns.map((col) => (
                <div
                  key={`${col.title}-${row.key}`}
                  className={`px-6 py-5 border-l ${last ? '' : 'border-b'} border-[#D3C6AC] ${cellBg(col)}`}
                >
                  {row.key === 'maintenance' ? (
                    <Dots filled={col.maintenance.dots} label={col.maintenance.label} />
                  ) : (
                    <p className="text-[#3D3A36] text-[14px] leading-[1.5]">{col[row.key]}</p>
                  )}
                </div>
              ))}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  </>
);

export default ComparisonMatrix;
